import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { withStyles } from '@material-ui/styles';

import ConsultationCTA from 'Containers/ConsultationCTA';
import Typography from 'Components/Typography';
import TwoUp from 'Components/TwoUp';

import useMedia from 'utils/useMedia';
import { breakpoints } from 'Components/ThemeProvider';
import SliderVariant from 'Components/SliderVariant';
import Carousel from 'Components/Carousel';
import BlockLayout from './BlockLayout';

const styles = theme => ({
  root: {
    transition: 'background',
    transitionDuration: 300,
    willChange: 'background',
    '& h2': {
      [theme.breakpoints.match('sm')]: {
        fontSize: 36,
      },
    },
  },
  slide: {
    height: '100%',
    width: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textShadow: ' 0px 2px 40px #00000020, 0px 2px 5px #00000030',
    paddingTop: theme.props.verticalSpacingMob,
    paddingBottom: theme.props.verticalSpacingMob,
  },
  sliderRoot: {
    height: '100%',
  },
  sliderSlide: {
    height: '100%',
    width: '100%',
  },
  label: {
    display: 'flex',
    marginBottom: 50,
  },
  labelCode: {
    ...theme.typography.mono3,
    color: theme.palette.common.sorbet.light,
    fontWeight: 'bold',
    alignSelf: 'flex-start',
    margin: 0,
    lineHeight: 'inherit',
    marginRight: 8,
    paddingTop: 5,
  },
  labelName: {
    ...theme.typography.mono1,
    fontSize: 24,
    color: theme.palette.common.white.light,
    alignSelf: 'flex-start',
    margin: 0,
    lineHeight: 'inherit',
  },
});

const TextImageSlider = ({ classes, content: { slides, text }, dataFrom }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);

  return (
    <TwoUp ref={ref} backgroundColor="white" classes={{ root: classes.root }} wideScreenOrder="21">
      {isMobile ? (
        <Carousel
          key="press-carousel"
          arrows={false}
          controlsPosition="absolute"
          controlsTheme="rectangleWhite"
          dataFrom="block-press-reviews"
          heap="Press card"
          isLooping
          width={isMobile ? '100%' : '680px'}
        >
          {slides.map(slide => (
            <div
              key={slide.labels[0].name}
              className={classes.slide}
              style={{
                backgroundImage: inView ? `url(${slide.background})` : undefined,
              }}
            >
              {slide.labels.map(label => (
                <span key={label.name} className={classes.label}>
                  <span className={classes.labelCode}>{label.code}</span>
                  <p className={classes.labelName}>{label.name}</p>
                </span>
              ))}
            </div>
          ))}
        </Carousel>
      ) : (
        <SliderVariant
          autoPlay
          classes={{ root: classes.sliderRoot, slide: classes.sliderSlide }}
          controlsTheme="rectangleWhite"
          floatingControls
        >
          {slides.map(slide => (
            <div
              key={slide.labels[0].name}
              className={classes.slide}
              style={{
                backgroundImage: inView ? `url(${slide.background})` : undefined,
              }}
            >
              {slide.labels.map(label => (
                <span key={label.name} className={classes.label}>
                  <span className={classes.labelCode}>{label.code}</span>
                  <p className={classes.labelName}>{label.name}</p>
                </span>
              ))}
            </div>
          ))}
        </SliderVariant>
      )}

      <BlockLayout contentAlign="left" contentAlignMob="center" contentWidth="half">
        <Typography color="noir" markupName="h2" paragraph variant="hero2">
          {text.title}
        </Typography>
        <Typography color="noir" paragraph variant="p2">
          {text.description}
        </Typography>

        {!isMobile && <ConsultationCTA dataFrom={dataFrom} marginTop theme="noir" />}
      </BlockLayout>
    </TwoUp>
  );
};

TextImageSlider.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  content: PropTypes.shape({
    text: PropTypes.object.isRequired,
    slides: PropTypes.array.isRequired,
  }).isRequired,
  dataFrom: PropTypes.string,
};

TextImageSlider.defaultProps = {
  dataFrom: 'block-slider',
};

export default withStyles(styles)(TextImageSlider);
